/* Pixiwoo — Winter Light collection page (templates/collection.winter-light.json):
   sticky-chip offset, client-side chip filter with URL-hash state, Show all, light-tile placement.
   No dependencies. Every pxw-xm-* section emits this script tag so each also works standalone in
   the Theme editor; the browser fetches the file once and we bail out after the first run. */
(() => {
  if (window.__pxwXmInit) return;
  window.__pxwXmInit = true;

  const reduce = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // 'instant' (not 'auto'): 'auto' defers to assets/base.css html { scroll-behavior: smooth },
  // so reduced-motion visitors would still get an animated scroll.
  const behavior = () => (reduce() ? 'instant' : 'smooth');

  /* ---------- Sticky offset: the chips sit directly under the theme's sticky header ---------- */
  let stickyTop = 0;
  function watchHeader() {
    const root = document.documentElement;
    const header = document.querySelector('.header-section');
    let lastTop = null;
    let lastVw = null;
    const update = () => {
      stickyTop = 0;
      if (header && getComputedStyle(header).position === 'sticky') {
        // The header sticks at top: -1px, so once it is stuck its visible bottom edge is
        // height - 1; before that (page at the top) it is the full height. Measure the edge.
        const rect = header.getBoundingClientRect();
        stickyTop = Math.max(0, Math.round(rect.top <= 0 ? rect.bottom : rect.height - 1));
      }
      if (stickyTop !== lastTop) {
        lastTop = stickyTop;
        root.style.setProperty('--pxw-xm-sticky-top', `${stickyTop}px`);
      }
      // Viewport width without the scrollbar, so the 1200px column and the bleed padding agree.
      if (root.clientWidth !== lastVw) {
        lastVw = root.clientWidth;
        root.style.setProperty('--pxw-xm-vw', `${lastVw}px`);
      }
    };
    update();
    if (header && 'ResizeObserver' in window) new ResizeObserver(update).observe(header);
    // "Show on scroll up" toggles data-sticky-state without changing any size.
    if (header && 'MutationObserver' in window) {
      new MutationObserver(update).observe(header, { attributes: true, attributeFilter: ['data-sticky-state', 'sticky'], subtree: true });
    }
    window.addEventListener('resize', update, { passive: true });
    // The compact header changes height while scrolling; re-measure once per frame.
    let queued = false;
    window.addEventListener('scroll', () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        update();
      });
    }, { passive: true });
  }

  /* ---------- The grid ---------- */
  function initIndex(root) {
    if (root.dataset.pxwXmReady) return;
    root.dataset.pxwXmReady = '1';

    const grid = root.querySelector('[data-pxw-xm-grid]');
    if (!grid) return;
    const cards = Array.from(grid.querySelectorAll('[data-pxw-xm-card]'));
    const tile = grid.querySelector('[data-pxw-xm-tile]');
    const chips = Array.from(root.querySelectorAll('[data-pxw-xm-chip]'));
    const chipsBar = root.querySelector('[data-pxw-xm-chips]');
    const moreWrap = root.querySelector('[data-pxw-xm-more-wrap]');
    const status = root.querySelector('[data-pxw-xm-status]');
    const visibleCount = parseInt(root.dataset.visible, 10) || 16;
    const tileAt = parseInt(root.dataset.tileAt, 10) || 8;
    const keys = new Set(chips.map((chip) => chip.dataset.pxwXmChip));
    let current = 'all';
    let expanded = false;

    const matches = (card, key) => key === 'all' || (card.dataset.keys || '').split(' ').includes(key);

    // Light tile: home = after card N. With a chip active it stays there only if at least N
    // visible cards precede it; otherwise it goes after the last visible card.
    function placeTile(key) {
      if (!tile) return;
      const home = cards[Math.min(tileAt, cards.length) - 1];
      if (home && home.nextElementSibling !== tile) home.after(tile);
      if (key === 'all') {
        tile.hidden = false;
        return;
      }
      const visible = cards.filter((card) => !card.hidden);
      const before = visible.filter((card) => card.compareDocumentPosition(tile) & Node.DOCUMENT_POSITION_FOLLOWING).length;
      if (before < tileAt && visible.length) visible[visible.length - 1].after(tile);
      tile.hidden = visible.length === 0;
    }

    function apply(key) {
      if (!keys.has(key)) key = 'all';
      current = key;
      chips.forEach((chip) => {
        const on = chip.dataset.pxwXmChip === key;
        chip.setAttribute('aria-pressed', String(on));
        if (chip.hasAttribute('data-pxw-xm-hidden-chip')) chip.hidden = !on;
      });
      cards.forEach((card, i) => {
        let show = matches(card, key);
        if (key === 'all' && !expanded) show = i < visibleCount;
        card.hidden = !show;
      });
      if (moreWrap) moreWrap.hidden = !(key === 'all' && !expanded && cards.length > visibleCount);
      placeTile(key);
      root.dataset.filter = key;
      // Screen readers: the grid changed under the chips.
      if (status) status.textContent = `${cards.filter((card) => !card.hidden).length} of ${cards.length} shown`;
    }

    function scrollToGrid() {
      const top = root.getBoundingClientRect().top + window.scrollY - stickyTop;
      window.scrollTo({ top: Math.max(0, top), behavior: behavior() });
    }

    // After a chip click deep in the grid, make sure the (now shorter) grid starts under the chips.
    function scrollToGridIfPast() {
      if (!chipsBar) return;
      const chipsBottom = chipsBar.getBoundingClientRect().bottom;
      const top = grid.getBoundingClientRect().top;
      if (top < chipsBottom) window.scrollBy({ top: top - chipsBottom - 8, behavior: behavior() });
    }

    function setHash(key) {
      const url = key === 'all' ? location.pathname + location.search : `#${key}`;
      history.replaceState(history.state, '', url);
    }

    // In-page jumps (hero button → #pxw-xm-grid, light tile → #pxw-xm-homes) scroll without
    // writing a hash, so the hash means "the active chip" and nothing else.
    document.addEventListener('click', (event) => {
      if (!root.isConnected) return; // theme editor re-rendered the section; this closure is stale
      const jump = event.target.closest('a[href^="#pxw-xm-"]');
      if (!jump || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const target = document.getElementById(jump.getAttribute('href').slice(1));
      // A missing target (e.g. the real-homes gallery is not rendered) must not write a hash
      // that the chip logic would later read.
      event.preventDefault();
      if (!target) return;
      const top = target.getBoundingClientRect().top + window.scrollY - stickyTop;
      window.scrollTo({ top: Math.max(0, top), behavior: behavior() });
    });

    root.addEventListener('click', (event) => {
      const chip = event.target.closest('[data-pxw-xm-chip]');
      if (chip) {
        apply(chip.dataset.pxwXmChip);
        setHash(current);
        scrollToGridIfPast();
        return;
      }
      if (event.target.closest('[data-pxw-xm-more]')) {
        expanded = true;
        apply(current);
        const first = cards[visibleCount] && cards[visibleCount].querySelector('a');
        if (first) first.focus({ preventScroll: true });
      }
    });

    // Recipient tiles (and any other link carrying data-pxw-xm-filter) select a chip and scroll up.
    document.addEventListener('click', (event) => {
      if (!root.isConnected) return;
      const link = event.target.closest('a[data-pxw-xm-filter]');
      if (!link) return;
      // Let the browser handle Cmd/Ctrl/Shift/Alt and middle clicks (open in a new tab).
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const wanted = link.dataset.pxwXmFilter;
      const key = keys.has(wanted) ? wanted : 'all';
      event.preventDefault();
      apply(key);
      const url = key === 'all' ? location.pathname + location.search : `#${key}`;
      if (location.hash !== (key === 'all' ? '' : `#${key}`)) history.pushState(history.state, '', url);
      scrollToGrid();
    });

    // URL hash = chip state (#cardinals). Other hashes (#pxw-xm-grid, #pxw-xm-homes) are left alone.
    const fromHash = () => {
      const key = location.hash.replace(/^#/, '');
      if (keys.has(key)) {
        apply(key);
        return true;
      }
      // An empty fragment means no chip is selected; any other fragment is left alone.
      if (!key && current !== 'all') apply('all');
      return false;
    };
    window.addEventListener('hashchange', () => {
      if (!root.isConnected) return;
      if (fromHash()) scrollToGrid();
    });
    window.addEventListener('popstate', () => {
      if (!root.isConnected) return;
      fromHash();
    });

    apply('all');
    if (fromHash()) {
      requestAnimationFrame(scrollToGrid);
      // Images and fonts settle after 'load' and move the grid; re-align then, but only while the
      // visitor has not taken over.
      let touched = false;
      const mark = () => {
        touched = true;
      };
      ['wheel', 'touchstart', 'keydown', 'pointerdown'].forEach((t) => window.addEventListener(t, mark, { once: true, passive: true }));
      window.addEventListener('load', () => {
        if (!touched) scrollToGrid();
      }, { once: true });
    }
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-pxw-xm-index]').forEach(initIndex);
  }

  const start = () => {
    watchHeader();
    init();
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // Theme editor: sections re-render in place
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
